// client/src/hooks/useMessageRealtime.ts
// Hook for subscribing to realtime message updates with instant cache patching

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { realtimeClient } from '../realtime/socketClient';
import {
  RealtimeEventType,
  MessagePayload,
  MessageStatusPayload,
  ThreadPayload,
  ThreadReadPayload,
  ThreadParticipantSeenPayload,
  MessageReactionPayload,
} from '../realtime/events';
import { uniboxApi } from '../api/uniboxApi';
import {
  applyThreadCreated,
  applyMessageCreated,
  applyMessageCreatedToThreadList,
  applyThreadReadToThreadList,
  applyMessageStatusChanged,
  applyMessageReactionChanged,
} from '../realtime/cachePatchHelpers';
import type { ThreadsResponse, MessagesResponse, ListThreadsParams, ListMessagesParams } from '../types/api';

interface UseMessageRealtimeOptions {
  workspaceId: string;
  /** Currently active/viewed thread ID (won't increment unread for this thread) */
  activeThreadId?: string | null;
  /** Current user's ID (won't increment unread for own messages) */
  currentUserId?: string | null;
  onThreadCreated?: (payload: ThreadPayload) => void;
  onMessageCreated?: (payload: MessagePayload) => void;
  onMessageStatusChanged?: (payload: MessageStatusPayload) => void;
  onThreadUpdated?: (payload: ThreadPayload) => void;
  onThreadRead?: (payload: ThreadReadPayload) => void;
  onThreadParticipantSeen?: (payload: ThreadParticipantSeenPayload) => void;
  onMessageReactionChanged?: (payload: MessageReactionPayload) => void;
}

/**
 * Hook to subscribe to realtime message events and instantly
 * patch RTK Query cache (no refetch required).
 *
 * For instant updates:
 * - THREAD_CREATED: Inserts new thread at top of list
 * - MESSAGE_CREATED: Appends message, updates thread preview/unread
 * - THREAD_READ: Resets unread count
 * - MESSAGE_STATUS_CHANGED: Updates message status
 *
 * Fallback to invalidation only when cache entry doesn't exist.
 */
export function useMessageRealtime({
  workspaceId,
  activeThreadId,
  currentUserId,
  onThreadCreated,
  onMessageCreated,
  onMessageStatusChanged,
  onThreadUpdated,
  onThreadRead,
  onThreadParticipantSeen,
  onMessageReactionChanged,
}: UseMessageRealtimeOptions) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const callbacksRef = useRef({
    onThreadCreated,
    onMessageCreated,
    onMessageStatusChanged,
    onThreadUpdated,
    onThreadRead,
    onThreadParticipantSeen,
    onMessageReactionChanged,
  });

  // Track current activeThreadId in a ref for use in callbacks
  const activeThreadIdRef = useRef(activeThreadId);
  const currentUserIdRef = useRef(currentUserId);

  // Update refs when values change
  useEffect(() => {
    callbacksRef.current = {
      onThreadCreated,
      onMessageCreated,
      onMessageStatusChanged,
      onThreadUpdated,
      onThreadRead,
      onThreadParticipantSeen,
      onMessageReactionChanged,
    };
  }, [onThreadCreated, onMessageCreated, onMessageStatusChanged, onThreadUpdated, onThreadRead, onThreadParticipantSeen, onMessageReactionChanged]);

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  // Handle thread created event - INSTANT PATCH
  const handleThreadCreated = useCallback(
    (payload: ThreadPayload) => {
      if (payload.workspaceId !== workspaceId) return;

      let patchedAny = false;

      // Patch getThreads cache for this workspace
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getThreads',
            { workspaceId } as ListThreadsParams,
            (draft: ThreadsResponse) => {
              const result = applyThreadCreated(draft, payload);
              if (result) {
                Object.assign(draft, result);
                patchedAny = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Also try patching general list queries
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getThreads',
            {} as ListThreadsParams,
            (draft: ThreadsResponse) => {
              if (payload.workspaceId === workspaceId) {
                const result = applyThreadCreated(draft, payload);
                if (result) {
                  Object.assign(draft, result);
                  patchedAny = true;
                }
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Fallback: invalidate if no cache was patched
      if (!patchedAny) {
        dispatch(
          uniboxApi.util.invalidateTags([{ type: 'Thread', id: 'LIST' }])
        );
      }

      callbacksRef.current.onThreadCreated?.(payload);
    },
    [workspaceId, dispatch]
  );

  // Handle message created event - INSTANT PATCH
  const handleMessageCreated = useCallback(
    (payload: MessagePayload) => {
      if (payload.workspaceId !== workspaceId) return;

      let patchedMessages = false;
      let patchedThreadList = false;

      // Patch messages cache for this thread
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getMessages',
            { threadId: payload.threadId } as ListMessagesParams,
            (draft: MessagesResponse) => {
              const result = applyMessageCreated(draft, payload);
              if (result) {
                Object.assign(draft, result);
                patchedMessages = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Patch thread list cache (update preview, move to top, handle unread)
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getThreads',
            { workspaceId } as ListThreadsParams,
            (draft: ThreadsResponse) => {
              const result = applyMessageCreatedToThreadList(draft, payload, {
                activeThreadId: activeThreadIdRef.current,
                currentUserId: currentUserIdRef.current,
              });
              if (result) {
                Object.assign(draft, result);
                patchedThreadList = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Also try general list
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getThreads',
            {} as ListThreadsParams,
            (draft: ThreadsResponse) => {
              const result = applyMessageCreatedToThreadList(draft, payload, {
                activeThreadId: activeThreadIdRef.current,
                currentUserId: currentUserIdRef.current,
              });
              if (result) {
                Object.assign(draft, result);
                patchedThreadList = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Fallback: invalidate caches that couldn't be patched
      if (!patchedMessages) {
        dispatch(
          uniboxApi.util.invalidateTags([{ type: 'Message', id: payload.threadId }])
        );
      }
      if (!patchedThreadList) {
        dispatch(
          uniboxApi.util.invalidateTags([{ type: 'Thread', id: 'LIST' }])
        );
      }

      callbacksRef.current.onMessageCreated?.(payload);
    },
    [workspaceId, dispatch]
  );

  // Handle message status changed event - INSTANT PATCH
  const handleMessageStatusChanged = useCallback(
    (payload: MessageStatusPayload) => {
      if (payload.workspaceId !== workspaceId) return;

      let patched = false;

      // Patch messages cache
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getMessages',
            { threadId: payload.threadId } as ListMessagesParams,
            (draft: MessagesResponse) => {
              const result = applyMessageStatusChanged(
                draft,
                payload.messageId,
                payload.status,
                payload.errorMessage,
                payload.deliveredAt,
                payload.readAt
              );
              if (result) {
                Object.assign(draft, result);
                patched = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Fallback
      if (!patched) {
        dispatch(
          uniboxApi.util.invalidateTags([
            { type: 'Message', id: payload.messageId },
            { type: 'Message', id: payload.threadId },
          ])
        );
      }

      callbacksRef.current.onMessageStatusChanged?.(payload);
    },
    [workspaceId, dispatch]
  );

  // Handle thread updated event - INSTANT PATCH
  const handleThreadUpdated = useCallback(
    (payload: ThreadPayload) => {
      if (payload.workspaceId !== workspaceId) return;

      let patched = false;

      // Patch thread list (update existing thread)
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getThreads',
            { workspaceId } as ListThreadsParams,
            (draft: ThreadsResponse) => {
              const result = applyThreadCreated(draft, payload); // Uses upsert logic
              if (result) {
                Object.assign(draft, result);
                patched = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Fallback
      if (!patched) {
        dispatch(
          uniboxApi.util.invalidateTags([
            { type: 'Thread', id: 'LIST' },
            { type: 'Thread', id: payload.threadId },
          ])
        );
      }

      callbacksRef.current.onThreadUpdated?.(payload);
    },
    [workspaceId, dispatch]
  );

  // Handle thread read event - INSTANT PATCH
  const handleThreadRead = useCallback(
    (payload: ThreadReadPayload) => {
      if (payload.workspaceId !== workspaceId) return;

      let patched = false;

      // Patch thread list
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getThreads',
            { workspaceId } as ListThreadsParams,
            (draft: ThreadsResponse) => {
              const result = applyThreadReadToThreadList(draft, payload.threadId, payload.unreadCount);
              if (result) {
                Object.assign(draft, result);
                patched = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Fallback
      if (!patched) {
        dispatch(
          uniboxApi.util.invalidateTags([
            { type: 'Thread', id: payload.threadId },
            { type: 'Thread', id: 'LIST' },
          ])
        );
      }

      callbacksRef.current.onThreadRead?.(payload);
    },
    [workspaceId, dispatch]
  );

  // Handle thread participant seen event - Just pass through to callback
  // Actual cache update is handled by component using onThreadParticipantSeen
  const handleThreadParticipantSeen = useCallback(
    (payload: ThreadParticipantSeenPayload) => {
      if (payload.workspaceId !== workspaceId) return;
      callbacksRef.current.onThreadParticipantSeen?.(payload);
    },
    [workspaceId]
  );

  // Handle message reaction changed event - INSTANT PATCH
  const handleMessageReactionChanged = useCallback(
    (payload: MessageReactionPayload) => {
      if (payload.workspaceId !== workspaceId) return;

      let patched = false;

      // Patch messages cache for this thread
      try {
        dispatch(
          uniboxApi.util.updateQueryData(
            'getMessages',
            { threadId: payload.threadId } as ListMessagesParams,
            (draft: MessagesResponse) => {
              const result = applyMessageReactionChanged(draft, payload);
              if (result) {
                Object.assign(draft, result);
                patched = true;
              }
            }
          )
        );
      } catch {
        // Cache entry doesn't exist
      }

      // Fallback: invalidate if no cache was patched
      if (!patched) {
        dispatch(
          uniboxApi.util.invalidateTags([
            { type: 'Message', id: payload.messageId },
            { type: 'Message', id: payload.threadId },
          ])
        );
      }

      callbacksRef.current.onMessageReactionChanged?.(payload);
    },
    [workspaceId, dispatch]
  );

  // Subscribe to realtime events
  useEffect(() => {
    const topic = `workspace:${workspaceId}`;

    // Subscribe to workspace
    realtimeClient.subscribe(topic);

    // Set up event listeners
    const unsubThreadCreated = realtimeClient.on<ThreadPayload>(
      topic,
      handleThreadCreated,
      RealtimeEventType.THREAD_CREATED
    );

    const unsubMessage = realtimeClient.on<MessagePayload>(
      topic,
      handleMessageCreated,
      RealtimeEventType.MESSAGE_CREATED
    );

    const unsubStatus = realtimeClient.on<MessageStatusPayload>(
      topic,
      handleMessageStatusChanged,
      RealtimeEventType.MESSAGE_STATUS_CHANGED
    );

    const unsubThread = realtimeClient.on<ThreadPayload>(
      topic,
      handleThreadUpdated,
      RealtimeEventType.THREAD_UPDATED
    );

    const unsubThreadRead = realtimeClient.on<ThreadReadPayload>(
      topic,
      handleThreadRead,
      RealtimeEventType.THREAD_READ
    );

    const unsubParticipantSeen = realtimeClient.on<ThreadParticipantSeenPayload>(
      topic,
      handleThreadParticipantSeen,
      RealtimeEventType.THREAD_PARTICIPANT_SEEN
    );

    const unsubReactionChanged = realtimeClient.on<MessageReactionPayload>(
      topic,
      handleMessageReactionChanged,
      RealtimeEventType.MESSAGE_REACTION_CHANGED
    );

    return () => {
      unsubThreadCreated();
      unsubMessage();
      unsubStatus();
      unsubThread();
      unsubThreadRead();
      unsubParticipantSeen();
      unsubReactionChanged();
      realtimeClient.unsubscribe(topic);
    };
  }, [
    workspaceId,
    handleThreadCreated,
    handleMessageCreated,
    handleMessageStatusChanged,
    handleThreadUpdated,
    handleThreadRead,
    handleThreadParticipantSeen,
    handleMessageReactionChanged,
  ]);
}

export default useMessageRealtime;
